import React, { useCallback, useEffect, useRef, useState } from "react";
import MerchantsService from "../../../api/merchant";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import { alpha, useTheme } from "@mui/material/styles";

// ICONS
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";

import { ButtonNoShadow } from "../../styled/button";
import { CustomModalRoot } from "../../styled/modal";

// catalog_imports.status integers (mirror dispatcher.models.CatalogImports)
const STATUS = { CREATED: 0, IMPORTING: 1, SUCCESS: 2, ERROR: 3 };
const POLL_INTERVAL_MS = 2000;

export default function ImportProgressModal({ show, restaurant, onClose }) {
  const theme = useTheme();
  const ifoodId = restaurant?.ifood_id;

  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const timerRef = useRef(null);
  const mountedRef = useRef(false);

  const status = data?.status;
  const isTerminal = status === STATUS.SUCCESS || status === STATUS.ERROR;
  const total = data?.total_items ?? null;
  const processed = data?.processed_items ?? 0;
  const percent =
    status === STATUS.SUCCESS
      ? 100
      : typeof data?.progress_percent === "number"
      ? data.progress_percent
      : total
      ? Math.min(100, Math.round((processed / total) * 100))
      : 0;
  // Determinate only once we know the total; before that the worker is still
  // fetching catalogs, so show an indeterminate bar.
  const determinate = !!total || status === STATUS.SUCCESS;

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!ifoodId) return;
    try {
      const res = await MerchantsService.getMerchantsIfoodImport({
        ifood_id: ifoodId,
      });
      if (!mountedRef.current) return;
      const payload = Array.isArray(res.data) ? null : res.data;
      setData(payload);
      if (
        payload &&
        (payload.status === STATUS.SUCCESS || payload.status === STATUS.ERROR)
      ) {
        stopPolling();
      }
    } catch (e) {
      if (!mountedRef.current) return;
      setError(true);
      stopPolling();
    }
  }, [ifoodId, stopPolling]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  useEffect(() => {
    if (!show || !ifoodId) return undefined;
    setError(false);
    fetchStatus();
    timerRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return stopPolling;
  }, [show, ifoodId, fetchStatus, stopPolling]);

  const handleRetry = async () => {
    setRetrying(true);
    setError(false);
    setData(null);
    try {
      await MerchantsService.createMerchantsIfoodImport(restaurant);
      fetchStatus();
      timerRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    } catch (e) {
      console.error(
        "iFood catalog re-import failed",
        e?.response?.status,
        e?.response?.data || e?.message
      );
      setError(true);
    } finally {
      setRetrying(false);
    }
  };

  const handleClose = () => {
    stopPolling();
    onClose && onClose(status === STATUS.SUCCESS);
  };

  // ---- Header (icon + colour + label) by state -------------------------
  let accent = theme.palette.primary.main;
  let HeaderIcon = CloudDownloadIcon;
  let label = "Importando cardápio";
  let helper = "Conectando ao iFood…";

  if (error || status === STATUS.ERROR) {
    accent = theme.palette.error.main;
    HeaderIcon = ErrorOutlineIcon;
    label = "Falha na importação";
    helper = error
      ? "Não foi possível obter o status da importação."
      : "Ocorreu um erro ao importar o cardápio. Tente novamente.";
  } else if (status === STATUS.SUCCESS) {
    accent = theme.palette.success.main;
    HeaderIcon = CheckCircleIcon;
    label = "Importação concluída";
    helper = total
      ? `${total} ${total === 1 ? "item importado" : "itens importados"}.`
      : "Nenhum item encontrado no cardápio.";
  } else if (status === STATUS.IMPORTING) {
    helper = total ? "Importando itens do cardápio…" : "Lendo o cardápio…";
  } else {
    helper = "Aguardando início da importação…";
  }

  const showProgress = !error && status !== STATUS.ERROR;

  return (
    <Modal
      aria-labelledby="ifood-import-progress"
      open={!!show}
      onClose={isTerminal || error ? handleClose : undefined}
      closeAfterTransition
      sx={{ zIndex: 9999999, border: "none" }}
    >
      <Fade in={!!show}>
        <CustomModalRoot top={40} left={50} width={32} height={"auto"} maxheight={90}>
          <Box sx={{ p: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  display: "flex",
                  borderRadius: 2,
                  p: 1,
                  color: accent,
                  bgcolor: alpha(accent, 0.12),
                }}
              >
                <HeaderIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {label}
                </Typography>
                {restaurant?.name && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {restaurant.name}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {showProgress && (
              <Box>
                <LinearProgress
                  variant={determinate ? "determinate" : "indeterminate"}
                  value={determinate ? percent : undefined}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: alpha(accent, 0.15),
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 5,
                      backgroundColor: accent,
                    },
                  }}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mt: 1 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {total !== null
                      ? `${processed} / ${total} itens`
                      : "Calculando total…"}
                  </Typography>
                  {determinate && (
                    <Typography variant="caption" fontWeight={700} sx={{ color: accent }}>
                      {percent}%
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}

            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mt: showProgress ? 2 : 0 }}
            >
              {helper}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              display: "flex",
              gap: 1,
              justifyContent: "flex-end",
            }}
          >
            {(error || status === STATUS.ERROR) && (
              <ButtonNoShadow
                size="large"
                fontWeight="bold"
                textColor={theme.palette.common.white}
                backgroundColor={theme.palette.primary.main}
                backgroundColorHover={theme.palette.primary.dark}
                disabled={retrying}
                onClick={handleRetry}
              >
                {retrying ? "Reenviando…" : "Tentar novamente"}
              </ButtonNoShadow>
            )}
            <ButtonNoShadow
              size="large"
              fontWeight="bold"
              backgroundColor={theme.palette.grey[300]}
              onClick={handleClose}
            >
              {isTerminal || error ? "Fechar" : "Continuar em segundo plano"}
            </ButtonNoShadow>
          </Box>
        </CustomModalRoot>
      </Fade>
    </Modal>
  );
}
